import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  resource,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";

import { PrivateQuery } from "../../../apis/networkdashboard/graphql/graphql";
import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { IPPrefixSubscription, StartLoadMessage } from "../../../helpers/self-service/models/types";
import { MessagePart, ModifyIpPrefixes } from "../../../helpers/self-service/modify_ip_prefixes";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { IP_PREFIXES_LIST_QUERY } from "../../../pages/ip-prefixes/ip-prefixes.rgql";
import { IpSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";

type SubscriptionInstanceId = string;
type PrefixSubscriptionId = string;
interface ServicePayloadItem {
  [x: SubscriptionInstanceId]: PrefixSubscriptionId[];
}

@Component({
  selector: "selfservice-ip-prefixes",
  templateUrl: "selfservice-ip-prefixes.html",
  styleUrl: "selfservice-ip-prefixes.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDropList,
    FormsModule,
    GuidPipe,
    MatTooltipModule,
    SelfserviceWizard,
    TranslatePipe,
  ],
})
export class SelfserviceIPPrefixes implements OnInit, OnDestroy {
  private apollo = inject(Apollo);
  private auth = inject(AuthService);

  protected subscription = input.required<IpSubscriptionDetails>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();
  protected initialSapIndex = input.required<number>();
  protected selectedPrefix = input.required<any>();
  protected initialCurrentStep = input(1);

  protected sapIndex = linkedSignal(() => this.initialSapIndex());
  protected saps = computed(() => this.subscription().saps);

  private ipPrefixResource = resource({
    params: () => ({
      customerId: this.auth.viewingCustomer().customerId,
      subscriptionId: this.subscription().subscriptionId,
    }),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo.query<Pick<PrivateQuery, "ipPrefixList">>({
          query: IP_PREFIXES_LIST_QUERY,
          variables: { customer: params.customerId },
        }),
      ).then(({ data }) => data?.ipPrefixList.page ?? []),
  });

  protected dataLoading = computed(() => this.ipPrefixResource.isLoading());

  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  protected subscriptionCopy: IpSubscriptionDetails & {
    _saps: (IpSubscriptionDetails["saps"][0] & { ip_prefixes: IPPrefixSubscription[] })[];
  };

  private addedPrefixes: Map<PrefixSubscriptionId, Array<SubscriptionInstanceId>> = new Map();
  private removedPrefixes: Map<PrefixSubscriptionId, Array<SubscriptionInstanceId>> = new Map();

  protected term = signal("");

  private availablePrefixMapping = signal<any[]>([]);

  protected fetchedPrefixes = computed(() => this.ipPrefixResource.value() ?? []);

  private selectedPort = computed(() => this.saps()[this.sapIndex()].port);
  protected multiplePorts = computed(() => this.saps().length > 1);
  protected portCustomerDescription = computed(() => this.selectedPort()?.customerDescription);
  protected portSubscriptionId = computed(() => this.selectedPort()?.subscriptionId);
  protected portCustomerFullname = computed(() => this.selectedPort()?.customer?.fullname);
  private address = computed(() => this.selectedPort()?.locations[0]?.address);
  protected portLocationCity = computed(() => this.address()?.city);
  protected portLocationStreet = computed(() => this.address()?.street);

  private currentSapSubscription = computed(() => this.subscriptionCopy._saps[this.sapIndex()].subscriptionInstanceId);
  protected availableItems = computed(() => this.availablePrefixMapping()[this.sapIndex()]);
  protected filteredAvailableItems = computed(() => {
    const t = this.term().trim().toLowerCase();
    return t ?
        (this.availableItems() ?? []).filter((item) => item.ip_prefix.prefix.toLowerCase().includes(t))
      : this.availableItems();
  });

  protected expandIndexes = signal<number[]>([]);
  protected selectedItems: IPPrefixSubscription[] = [];
  private originalSelectedItems = signal({});

  protected get didRemovePrefixes() {
    return Object.keys(this.originalSelectedItems()).some((_, i) => this.removedItems(i).length > 0);
  }

  private get servicePayload() {
    const added: ServicePayloadItem = {};
    for (const entry of this.addedPrefixes.entries()) {
      // for readability
      const sapSubscriptionInstanceIdList = entry[1];
      const prefixSubscriptionId = entry[0];
      sapSubscriptionInstanceIdList.forEach((sid: string) => {
        added[sid] = [...(added[sid] ?? []), prefixSubscriptionId];
      });
    }

    const removed: ServicePayloadItem = {};
    for (const entry of this.removedPrefixes.entries()) {
      // for readability
      const sapSubscriptionInstanceIdList = entry[1];
      const prefixSubscriptionId = entry[0];
      sapSubscriptionInstanceIdList.forEach((sid: string) => {
        removed[sid] = [...(removed[sid] ?? []), prefixSubscriptionId];
      });
    }

    return {
      added,
      removed,
    };
  }

  constructor() {
    effect(() => {
      this.subscriptionCopy = {
        ...this.subscription(),
        // @ts-ignore FIXME ip_prefixes does not have a prefix subscription id; how to get it?
        _saps: this.subscription().saps.map((s) => ({
          ...s,
          ip_prefixes: (s.ipPrefixes || []).map((p) => Object.assign({}, { ip_prefix: { prefix: p } })),
        })),
      };

      this.originalSelectedItems.set(
        Object.assign(
          {},
          this.subscriptionCopy._saps.map((item) => ({
            ip_prefixes: [...item.ip_prefixes],
          })),
        ),
      );
    });

    effect(() => {
      if (this.ipPrefixResource.hasValue()) {
        this.setupChoices();
      }
    });
  }

  ngOnInit() {
    this.getSelectedItems();

    // Fix for cdk drag and drop in combination with mat dialog
    const _topMargin = document.getElementsByTagName("html")[0].style.top;
    document.getElementsByTagName("html")[0].classList.add("scroll-fix");
    //document.getElementsByTagName("app-root")[0].setAttribute("style", "position: relative; top:" + topMargin);
  }

  ngOnDestroy() {
    // Fix for cdk drag and drop in combination with mat dialog
    //document.getElementsByTagName("app-root")[0].setAttribute("style", "position: static; top: 0;");
    document.getElementsByTagName("html")[0].classList.remove("scroll-fix");
  }

  protected matchPrefixes() {
    const prefixesInUse = [];
    this.subscriptionCopy._saps.map((sap) => {
      const sapPrefixes = sap.ip_prefixes.map((prefix) => prefix.subscriptionId);
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      prefixesInUse.push(...sapPrefixes);
    });
    const curatedList = new Set(prefixesInUse);

    return prefixesInUse.length === curatedList.size * this.subscription().saps.length;
  }

  private getSelectedItems() {
    if (!this.subscriptionCopy?._saps?.[this.sapIndex()]?.ip_prefixes) {
      return;
    }
    this.selectedItems = this.subscriptionCopy._saps[this.sapIndex()].ip_prefixes || [];
  }

  protected expand(i: number) {
    if (this.expandIndexes().includes(i)) {
      this.expandIndexes.update((indexes) => indexes.filter((item) => item !== i));
    } else {
      this.expandIndexes().push(i);
    }
  }

  protected countNewItems(newItems: IPPrefixSubscription[], i: number) {
    return newItems.filter((val) => !this.originalSelectedItems()[i].ip_prefixes.includes(val)).length;
  }

  protected removedItems(i: number) {
    const originalArray = this.originalSelectedItems()[i].ip_prefixes;
    const newArray = this.subscriptionCopy?._saps[i].ip_prefixes;

    const difference = originalArray.filter((x) => !newArray.includes(x));
    return difference;
  }

  protected readonly buildCommand = () => {
    const { added, removed } = this.servicePayload;
    const action =
      this.subscription().product.tag === "IPBGP" ?
        SelfServiceCommandKeys.ModifyIpPrefixesIpBgp
      : SelfServiceCommandKeys.ModifyIpPrefixesIpStatic;
    const modify = new ModifyIpPrefixes();
    for (const sapSubscriptionInstanceId in added) {
      modify.setForInstance(MessagePart.ADD, sapSubscriptionInstanceId, added[sapSubscriptionInstanceId]);
    }
    for (const sapSubscriptionInstanceId in removed) {
      modify.setForInstance(MessagePart.DELETE, sapSubscriptionInstanceId, removed[sapSubscriptionInstanceId]);
    }
    const command = new SelfServiceCommand(this.subscriptionCopy.subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };

  protected constraints() {
    return this.subscriptionCopy._saps.every((sap) => sap.ip_prefixes.length > 0);
  }

  protected isOriginalItem(prefix) {
    return (
      this.originalSelectedItems()[this.sapIndex()].ip_prefixes.filter((item) => item.ip_prefix.prefix === prefix)
        .length > 0
    );
  }

  protected isOriginalItemInSap(index: number, prefix: string) {
    return (
      this.originalSelectedItems()[index].ip_prefixes.filter((item) => item.ip_prefix.prefix === prefix).length > 0
    );
  }

  protected sortedPrefixes(prefix_list: Array<IPPrefixSubscription>) {
    // don't touch the original list. sort() will modify the original array.
    const clonedList = prefix_list.map((a) => a);
    // remove the subnet part (the '/26' in 'x.x.x.x/26')
    const removeSubnet = (ip: string) => ip.split(`/`)[0];
    // convert the IP address to its int32 representation to sort
    const ipToInt = (ip: string) =>
      removeSubnet(ip)
        .split(`.`)
        .map((i: string) => Number(i))
        .reduce((r, e) => r * 256 + +e);
    clonedList.sort(
      (a: IPPrefixSubscription, b: IPPrefixSubscription) => ipToInt(a.ip_prefix.prefix) - ipToInt(b.ip_prefix.prefix),
    );
    return clonedList;
  }

  private trackAddRemove(event: CdkDragDrop<IPPrefixSubscription[]>) {
    const draggedPrefix = event.previousContainer.data[event.previousIndex];

    // the droplist container will generate new ID's each time it's opened.
    // However, the order is always the same.
    const droplistContainerIds = [event.previousContainer.id, event.container.id].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
    const isAdded =
      event.previousContainer.id === droplistContainerIds[0] && event.container.id === droplistContainerIds[1];
    const isRemoved =
      event.previousContainer.id === droplistContainerIds[1] && event.container.id === droplistContainerIds[0];
    const original = this.isOriginalItemInSap(this.sapIndex(), draggedPrefix.ip_prefix.prefix);

    const addToMap = (map: Map<PrefixSubscriptionId, Array<SubscriptionInstanceId>>, key: string, value: string) => {
      if (map.has(key)) {
        // @ts-expect-error: map.get(key) is possibly undefined
        map.set(key, [...map.get(key), value]);
      } else {
        map.set(key, [value]);
      }
    };

    const removeFromMap = (
      map: Map<PrefixSubscriptionId, Array<SubscriptionInstanceId>>,
      key: string,
      value: string,
    ) => {
      if (!map.has(key)) {
        return;
      }
      map.set(
        key,
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        map.get(key).filter((sid) => sid !== value),
      );
    };

    if (!original) {
      if (isAdded) {
        addToMap(this.addedPrefixes, draggedPrefix.subscriptionId, this.currentSapSubscription());
      } else if (isRemoved) {
        removeFromMap(this.addedPrefixes, draggedPrefix.subscriptionId, this.currentSapSubscription());
        // not original, and removed from the 'active' list.
        // so, not a removed prefix, but rather _not an added_ prefix.
        this.addedPrefixes.delete(draggedPrefix.subscriptionId);
      }
    }
    if (original) {
      if (isRemoved) {
        addToMap(this.removedPrefixes, draggedPrefix.subscriptionId, this.currentSapSubscription());
      } else if (isAdded) {
        removeFromMap(this.removedPrefixes, draggedPrefix.subscriptionId, this.currentSapSubscription());
      }
    }
  }

  protected drop(event: CdkDragDrop<IPPrefixSubscription[]>) {
    if (event.previousContainer === event.container) {
      // just a rearrangement in the same list. No action needed.
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      this.trackAddRemove(event);
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  protected prevPort() {
    const portsTotal = this.saps().length;
    this.sapIndex.update((value) => (value === 0 ? portsTotal - 1 : value - 1));
    this.getSelectedItems();
  }

  protected nextPort() {
    const portsTotal = this.saps().length;
    this.sapIndex.update((value) => (value === portsTotal - 1 ? 0 : value + 1));
    this.getSelectedItems();
  }

  private addAdditionalPrefixData(allFetchedPrefixes: IPPrefixSubscription[]) {
    this.subscriptionCopy = {
      ...this.subscriptionCopy,
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      _saps: this.subscriptionCopy._saps.map((s) => ({
        ...s,
        ip_prefixes: (s.ip_prefixes || []).map((p) =>
          Object.assign(
            {},
            {
              ...p,
              subscriptionId: allFetchedPrefixes.find((fp) => fp.ip_prefix.prefix === p.ip_prefix.prefix)
                ?.subscriptionId,
            },
          ),
        ),
      })),
    };

    this.originalSelectedItems.set(
      Object.assign(
        {},
        this.subscriptionCopy._saps.map((item) => ({
          ip_prefixes: [...item.ip_prefixes],
        })),
      ),
    );
    this.getSelectedItems();
  }

  private setupChoices() {
    const getPrefixString = (sub) =>
      /* first from ipam */ sub.ipPrefix?.ipam?.prefix ?? /* last resort */ sub.description.split(" ").pop();
    const allFetchedPrefixes = this.fetchedPrefixes().map((sub) => ({
      ip_prefix: { prefix: getPrefixString(sub) },
      subscriptionId: sub.subscriptionId,
    }));

    this.addAdditionalPrefixData(allFetchedPrefixes);

    const availableFetchedPrefixes = allFetchedPrefixes.filter((sub) => {
      const ipPinnenPrefix = "145.98.";
      return !sub.ip_prefix.prefix.startsWith(ipPinnenPrefix);
    });

    if (this.selectedPrefix()) {
      const preSelectedPrefix = availableFetchedPrefixes.find(
        (prefix) => prefix.subscriptionId === this.selectedPrefix().subscriptionId,
      );

      this.subscriptionCopy._saps.forEach((sap) => {
        // @ts-ignore FIXME ip_prefixes does not have a prefix subscription id
        sap.ip_prefixes.push(preSelectedPrefix);
      });
      const sapInstanceIds = this.subscriptionCopy._saps.map((sap) => sap.subscriptionInstanceId);
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      this.addedPrefixes.set(preSelectedPrefix.subscriptionId, sapInstanceIds);
    }

    const usedPrefixMapping = this.subscriptionCopy._saps.map((sap) => sap.ip_prefixes);

    const availablePrefixMapping = usedPrefixMapping.map((sapPrefixes) => {
      const sapPrefixIps = sapPrefixes.map((prefix) => prefix.ip_prefix.prefix);
      const sapPrefixesIds = sapPrefixes.map((prefix) => prefix.subscriptionId);
      return availableFetchedPrefixes.filter(
        (prefix) => !sapPrefixesIds.includes(prefix.subscriptionId) && !sapPrefixIps.includes(prefix.ip_prefix.prefix),
      );
    });
    this.availablePrefixMapping.set(availablePrefixMapping);
  }
}
